import React, { useState } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Box,
  Paper,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  SaveTemplateRequest,
  TemplateType,
  TEMPLATE_TYPE_OPTIONS,
} from '../../types/template.types';

interface SaveTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (templateData: SaveTemplateRequest) => void;
  currentText: string;
  suggestedType?: TemplateType;
}

const SaveTemplateDialog: React.FC<SaveTemplateDialogProps> = ({
  open,
  onClose,
  onSave,
  currentText,
  suggestedType = 'general',
}) => {
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState<TemplateType>(suggestedType);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setTemplateName('');
    setTemplateType(suggestedType);
    setIsSubmitting(false);
    onClose();
  };

  const handleSave = async () => {
    if (!templateName.trim()) return;

    setIsSubmitting(true);

    const templateData: SaveTemplateRequest = {
      templateName: templateName.trim(),
      templateType,
      templateText: currentText,
    };

    try {
      await onSave(templateData);
      handleClose();
    } catch (error) {
      console.error('Failed to save template:', error);
      setIsSubmitting(false);
    }
  };

  const canSave = templateName.trim().length > 0 && currentText.trim().length > 0;

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1500, // Lower than chat window
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 'sm',
          borderRadius: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            pb: 0,
          }}
        >
          <Typography variant="h6">Save as Template</Typography>
          <IconButton onClick={handleClose} size="small" disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              autoFocus
              fullWidth
              label="Template Name"
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              placeholder="Enter a descriptive name for this template"
              disabled={isSubmitting}
              helperText="Choose a name that clearly describes this template's purpose"
            />

            <FormControl fullWidth disabled={isSubmitting}>
              <InputLabel>Template Type</InputLabel>
              <Select
                value={templateType}
                onChange={e => setTemplateType(e.target.value as TemplateType)}
                label="Template Type"
              >
                {TEMPLATE_TYPE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!canSave || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Template'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SaveTemplateDialog;
